import {
  CancelledError,
  MutationConfig,
  queryCache,
  useMutation,
} from 'react-query';

import { SchemaAddDeclarationResponse } from '../api-schema';
import { ElectionType, IError, electionTypeMap } from '../api-types';
import { fetcher } from '../fetcher';
import { handleQueryError } from '../query-error';
import { userQueryKey } from './useUser';

type DeclarationVariables = {
  partyId: string;
  electionType: ElectionType;
  returnUrl?: string;
};

type AddDeclarationOptions = MutationConfig<
  SchemaAddDeclarationResponse,
  IError,
  DeclarationVariables
> & {
  /**
   * Redirect to party details after a successful add. Defaults to true.
   */
  redirectToParty?: boolean;
  /**
   * Base path for party details. Defaults to '/om-partiet'.
   */
  partyDetailsBasePath?: string;

  /**
   * Replace the current history entry when following returnUrl.
   */
  replaceReturnUrl?: boolean;

  returnUrl?: string;
};

function delay(ms: number) {
  if (process.env.NODE_ENV === 'test') return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function useAddDeclaration(options?: AddDeclarationOptions) {
  return useMutation<
    SchemaAddDeclarationResponse,
    IError,
    DeclarationVariables
  >(
    async ({ partyId, electionType, returnUrl }) => {
      try {
        const [result] = await Promise.all([
          fetcher<SchemaAddDeclarationResponse>('/api/v1/declarations/add', {
            method: 'POST',
            body: JSON.stringify({ partyId, electionType }),
          }),
          // Ensure this request takes at least 1 second to complete
          delay(1000),
        ]);

        return result;
      } catch (error) {
        // Convert CancelledError to a regular error so onError/onSettled fire.
        if (error instanceof CancelledError) {
          const message =
            (error as Error).message ||
            (typeof error === 'string' ? error : String(error));
          const normalized = new Error(message) as IError;
          Object.assign(normalized, error);
          throw normalized;
        }
        throw error;
      }
    },
    {
      ...options,
      onSuccess: (data, variables) => {
        // After posting, we update the user, so it shows the new state
        queryCache.invalidateQueries(userQueryKey, { throwOnError: false });

        const result = options?.onSuccess?.(data, variables);

        const shouldRedirect = options?.redirectToParty ?? true;

        if (
          options?.returnUrl &&
          typeof window !== 'undefined' &&
          process.env.NODE_ENV !== 'test'
        ) {
          if (options.replaceReturnUrl) {
            window.location.replace(options.returnUrl);
          } else {
            window.location.assign(options.returnUrl);
          }
        } else if (
          shouldRedirect &&
          typeof window !== 'undefined' &&
          process.env.NODE_ENV !== 'test'
        ) {
          const basePath = options?.partyDetailsBasePath || '/om-partiet';
          const election = electionTypeMap[variables.electionType];
          const url = `${basePath}?election=${election}&party=${variables.partyId}`;
          window.location.assign(url);
        }

        return result;
      },
      onError: (error, variables, snapshotValue) => {
        handleQueryError(error);
        // Make sure we still have a valid user
        queryCache.invalidateQueries(userQueryKey, { throwOnError: false });

        if (options && options.onError) {
          return options.onError(error, variables, snapshotValue);
        }
      },
    },
  );
}

export default useAddDeclaration;
